import StairIcon from '../../../assets/icons/stair.svg?react';
import { getSketchObjectWidthProperty } from '@pointorama/potree-shared';
import { defineSketchObjectType, registerSketchObjectType } from '../registry';
import { SketchObjectAnchorKind, SketchObjectCategory, type SketchObjectFrame } from '../types';
import { at, pushRect, pushSegment } from '../glyphs/frame';
import { getNumberParamProperty, type ParamValues } from '../paramSchema';
import { t } from '../../translation/markers';

// Step + step count sit ABOVE "distance to wall" (order 12), right after the
// shared width/depth rows (10-11).
const PARAMS = {
  treadDepth: { kind: 'float', label: t('step'), default: 0.25, min: 0.15, max: 0.4, decimals: 2, distance: true, order: 11.4 },
  stepCount: { kind: 'int', label: t('number of steps'), default: 11, min: 1, order: 11.5 },
  reversed: { kind: 'boolean', label: t('reverse direction'), default: false },
} as const;

/** Two coupled step values never fight below this difference (m). */
const STEP_EPS = 1e-4;

/** True while syncStairParams itself writes `stepCount` — the re-entrant
 *  'stepCount' notification must NOT re-quantize the step then (notify is
 *  synchronous, so a plain flag suffices). */
let internalCountWrite = false;

/**
 * Keep `step` (tread depth) and `number of steps` consistent with each other
 * and with the run length. The STEP is the user-authored invariant:
 *  - step or run length changed (panel edit, scale gizmo, resize handles) →
 *    count := round(run / step). The step itself is never touched, so
 *    resizing a stair changes how MANY treads it has, not their size (the
 *    drawn treads stretch evenly to fill the run in between).
 *  - number of steps edited directly → step := run / count (an explicit
 *    count edit means "divide this run into N treads").
 * Writes no-op when the values already agree (equal reactive-property writes
 * don't notify), which terminates the mutual updates.
 */
function syncStairParams(objectId: string, changedKey: string): void {
  if (changedKey !== 'width' && changedKey !== 'treadDepth' && changedKey !== 'stepCount') return;
  let runLength: number;
  try {
    runLength = getSketchObjectWidthProperty(objectId).value ?? 0;
  } catch {
    return; // property service not ready
  }
  if (runLength <= 0) return;
  const stepProperty = getNumberParamProperty('stair', 'treadDepth', objectId);
  const countProperty = getNumberParamProperty('stair', 'stepCount', objectId);
  if (changedKey === 'stepCount') {
    // A count change that merely follows a run/step change keeps the
    // authored step — only a direct count edit redistributes the run.
    if (internalCountWrite) return;
    const count = Math.max(1, Math.round(countProperty.value ?? 1));
    const step = runLength / count;
    if (Math.abs((stepProperty.value ?? 0) - step) > STEP_EPS) stepProperty.value = step;
  } else {
    const step = Math.max(0.05, stepProperty.value ?? 0.25);
    const count = Math.max(1, Math.round(runLength / step));
    if (countProperty.value !== count) {
      internalCountWrite = true;
      try {
        countProperty.value = count;
      } finally {
        internalCountWrite = false;
      }
    }
  }
}

/**
 * Stair run: LINE anchor + FREE constraint — the HYBRID placement UX. On a
 * wall the run is DRAWN like a cabinet (snap marker + rubber-band span,
 * aligned flush to the wall face, its length = the drawn span); away from
 * walls a single click STAMPS it at the default size, rotated with `R`. The
 * glyph skins the run length parametrically: the outline is filled with
 * tread lines derived from the tread depth, plus the standard up-direction
 * arrow along the centerline (walk line) from the start of the run (small
 * cross tick) toward local +X. Changing the width property or tread depth
 * reflows the treads.
 */
function buildStairGlyph(frame: SketchObjectFrame, params: ParamValues<typeof PARAMS>): number[] {
  const out: number[] = [];
  const hw = frame.width / 2;
  const hd = frame.depth / 2;

  pushRect(out, frame, -hw, hw, -hd, hd);

  // Treads: as many tread-depth cells as fit (at least one), stretched evenly
  // so they exactly fill the run.
  const treadDepth = Math.max(0.05, params.treadDepth);
  const treadCount = Math.max(1, Math.round(frame.width / treadDepth));
  const actualTread = frame.width / treadCount;
  for (let i = 1; i < treadCount; i++) {
    const u = -hw + i * actualTread;
    pushSegment(out, at(frame, u, -hd), at(frame, u, hd), frame.z);
  }

  // Walk line: centerline arrow pointing up the run, inset from the ends,
  // with a start tick and a chevron head. `reversed` flips it (local -X) so
  // the up direction can be corrected without rotating the whole run.
  const dirSign = params.reversed ? -1 : 1;
  const tail = -dirSign * hw * 0.85;
  const head = dirSign * hw * 0.85;
  const headSize = Math.min(frame.depth * 0.25, 0.15);
  pushSegment(out, at(frame, tail, 0), at(frame, head, 0), frame.z);
  pushSegment(out, at(frame, tail, -headSize), at(frame, tail, headSize), frame.z);
  pushSegment(out, at(frame, head, 0), at(frame, head - dirSign * headSize, headSize), frame.z);
  pushSegment(out, at(frame, head, 0), at(frame, head - dirSign * headSize, -headSize), frame.z);

  return out;
}

registerSketchObjectType(
  defineSketchObjectType({
    id: 'stair',
    label: t('Stair'),
    category: SketchObjectCategory.FIXTURES,
    anchor: SketchObjectAnchorKind.LINE,
    constraint: { kind: 'FREE' },
    defaultSize: { width: 2.8, depth: 0.9 },
    params: PARAMS,
    glyph: { kind: 'parametric', build: buildStairGlyph },
    // Simplified icon — the parametric glyph has too many treads at panel size.
    icon: StairIcon,
    syncParams: syncStairParams,
  }),
);
